import type { BombiixRow } from '../../../../../../Components/BombiixAGGrid/types';
import type { MeasurementsState, SpecGrading } from './types';
import { getProductHiddenSizeNames, getProductSizeNames } from './size-ranges';
import { isPushTargeting, isSelectDisabled, rowKeyOf } from './MeasurementsProvider';

/** Separator between the id segments (`devOrderId::rowKey`). */
export const ROW_ID_SEP = '::';

/** What a ticked checkbox means in the current push interaction. */
export type SelectRole = 'source' | 'target' | null;

/**
 * One AG-Grid row within a product block. Spec (POM) columns and Supporting-Document columns share the
 * same rows, zipped by index: row i shows POM[i] (when `hasPom`) and Document[i] (when `hasDoc`). A block
 * has `max(pomCount, docCount)` rows, so the shorter side leaves empty cells.
 */
export interface MeasurementGridRow extends BombiixRow {
  id: string;
  devOrderId: string;
  rowKey: string;
  hasPom: boolean;
  hasDoc: boolean;
  isFirstOfProduct: boolean;
  activeGrading: boolean;
  collapsed: boolean;
  selected: boolean;
  isSource: boolean;
  /** How this row's checkbox reads: the green push source, a blue push target, or a plain selection. */
  selectRole: SelectRole;
  /** True when the current push phase forbids changing this row's checkbox. */
  selectDisabled: boolean;
  number: string;
  image: string;
  gradingGroupName: string;
  /* Spec (POM) fields — populated when `hasPom`. */
  code: string;
  description: string;
  tolPlus: number | string | null;
  tolMinus: number | string | null;
  gradings: SpecGrading[];
  /** Size names this product actually grades — sizes outside it render as non-editable "not applicable". */
  productSizeRanges: string[];
  /** Size names hidden on this product's specification — their cells render blank (BM-10013). */
  hiddenSizeNames: string[];
  isUserCodeChanged: boolean;
  isUserDescChanged: boolean;
  /* Supporting-document fields — populated when `hasDoc`. */
  documentId: string;
  document: string;
  fileName: string;
  s3Key: string;
  mimetype: string;
  documentName: string;
  documentType: string;
  createdBy: string;
  createdAt: string;
  modifiedBy: string;
  modifiedAt: string;
}

/**
 * How a ticked checkbox reads. A push target (blue) exists only while a push is ARMED; merely ticking a
 * row names a source but starts no push, so until the button is pressed every tick keeps the default
 * green, whichever product it lands on.
 */
function selectRoleOf(isSelected: boolean, isTargeting: boolean, isSourceProduct: boolean): SelectRole {
  if (!isSelected) return null;
  return isTargeting && !isSourceProduct ? 'target' : 'source';
}

/**
 * Flatten module state into grid rows. Each product block has `max(POM rows, document rows)` rows;
 * the spec columns render POM[i] and the supporting-document columns render Document[i] on the same row,
 * so uneven counts leave the shorter side's cells blank. Product-level cells (image/number/grading group)
 * show only on the block's first row; a collapsed product contributes only that first row.
 */
export function buildGridRows(state: MeasurementsState): MeasurementGridRow[] {
  const collapsed = new Set(state.collapsedProducts);
  const selected = new Set(state.selectedRowIds);
  const targeting = isPushTargeting(state);
  const rows: MeasurementGridRow[] = [];

  for (const product of state.products) {
    const isCollapsed = collapsed.has(product.devOrderId);
    const isSource = state.sourceDevOrderId === product.devOrderId;
    const pomItems = product.specPomLibraries ?? [];
    const docs = state.supportingDocs[product.devOrderId] ?? [];
    /* Always at least one row: a product with neither POM rows nor documents still has to start a block,
       or it would vanish from the grid with no product number, no image and nowhere to upload its first
       supporting document. */
    const rowCount = Math.max(1, pomItems.length, docs.length);
    const productSizeRanges = getProductSizeNames(product);
    const hiddenSizeNames = getProductHiddenSizeNames(product);

    for (let index = 0; index < rowCount; index += 1) {
      if (isCollapsed && index > 0) break;
      const pom = pomItems[index];
      const doc = docs[index];
      const hasPom = Boolean(pom);
      const hasDoc = Boolean(doc);
      const rowKey = hasPom && pom ? rowKeyOf(pom, index) : `doc-${doc?._id ?? index}`;
      const id = `${product.devOrderId}${ROW_ID_SEP}${rowKey}`;
      rows.push({
        id,
        devOrderId: product.devOrderId,
        rowKey,
        hasPom,
        hasDoc,
        isFirstOfProduct: index === 0,
        activeGrading: state.activeGrading,
        collapsed: isCollapsed,
        selected: selected.has(id),
        isSource,
        /* Blue means "push target", which only exists once Push is armed — an un-armed selection is a plain
           bulk selection, however many products it spans, so every tick stays the default green. */
        selectRole: selectRoleOf(selected.has(id), targeting, isSource),
        selectDisabled: isSelectDisabled(state, product.devOrderId, index === 0),
        number: product.number,
        image: product.image,
        gradingGroupName: state.gradingGroupName,
        code: pom?.code ?? '',
        description: pom?.description ?? '',
        tolPlus: pom?.tolPlus ?? '',
        tolMinus: pom?.tolMinus ?? '',
        gradings: pom?.gradings ?? [],
        productSizeRanges,
        hiddenSizeNames,
        isUserCodeChanged: Boolean(pom?.isUserCodeChanged),
        isUserDescChanged: Boolean(pom?.isUserDescChanged),
        documentId: doc?._id ?? '',
        document: doc?.document ?? '',
        fileName: doc?.fileName ?? '',
        s3Key: doc?.s3Key ?? '',
        mimetype: doc?.mimetype ?? '',
        documentName: doc?.documentTitle ?? '',
        documentType: doc?.documentType ?? '',
        createdBy: doc?.createdBy ?? '',
        createdAt: doc?.createdAt ?? '',
        modifiedBy: doc?.modifiedBy ?? '',
        modifiedAt: doc?.modifiedAt ?? '',
      });
    }
  }

  return rows;
}
